import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { Button, Divider, Input, Menu, Tooltip, Typography } from 'antd';
import copy from 'copy-to-clipboard';

import Icon, {
  CheckOutlined,
  CloudDownloadOutlined,
  CopyOutlined,
  FacebookFilled,
  LaptopOutlined,
  LayoutOutlined,
  LinkedinFilled,
  LinkOutlined,
  MenuOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  QuestionCircleOutlined,
  TwitterOutlined,
  WhatsAppOutlined,
  GoogleOutlined,
} from '@ant-design/icons';

import 'react-intl-tel-input/dist/main.css';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import './style.less';

import { isEmpty } from 'lodash-es';
import { useMediaQuery } from 'react-responsive';
import { Trans, useTranslation } from 'react-i18next';
import { formByIdSelector } from '../../store/selectors/formSelector';
import { useParams } from 'react-router-dom';
import { useStore, useSelector } from 'react-redux';
import { Check, Copy, Layout, Link } from 'react-feather';
import useHorizontalScroll from '../../hooks/useHorizontalScroll';
import useWindowSize from '../../hooks/windowSize';
import { getFormLink } from '../../utils/helpers';

const { Paragraph, Text } = Typography;

const ShareFormMenu = ({ selectedScreen, setSelectedScreen }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  function handleMenuSelect(e) {
    setSelectedScreen(e.key);
  }
  const menuRef = useRef();
  const windowSize = useWindowSize();
  const [hasLeftScroll, hasRightScroll] = useHorizontalScroll(menuRef, windowSize);

  return (
    <div className="share-form-page-menu-container" ref={menuRef}>
      <Menu
        onSelect={handleMenuSelect}
        mode={'vertical'}
        selectedKeys={[selectedScreen]}
        className="share-form-page-menu"
      >
        <Menu.Item icon={<LinkOutlined />} key="link">
          {t('share.social.tabLabel')}
        </Menu.Item>
        <Menu.Item icon={<WhatsAppOutlined />} key="whatsapp">
          {t('share.whatsapp.tabLabel')}
        </Menu.Item>
        <Menu.Item icon={<LayoutOutlined />} key="website">
          {t('share.website.tabLabel')}
        </Menu.Item>
        <Menu.Item icon={<GoogleOutlined />} key="gtm">
          {t('share.gtm.tabLabel')}
        </Menu.Item>
        <Menu.Item icon={<i className="fa fa-wordpress" />} key="wordpress">
          {t('share.wordpress.tabLabel')}
        </Menu.Item>
        <Menu.Item icon={<QrcodeOutlined />} key="qr">
          QR code
        </Menu.Item>
      </Menu>
      {isMobile && hasRightScroll ? <div className="menu-scroll-indicator right"></div> : null}
      {isMobile && hasLeftScroll ? <div className="menu-scroll-indicator left"></div> : null}
    </div>
  );
};

const ShareForm = (props) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [widgetMessage, setWidgetMessage] = useState('');
  const [selectedScreen, setSelectedScreen] = useState('link');
  const params = useParams();
  const store = useStore();
  const form = formByIdSelector(store.getState(), params.id);
  const { me } = useSelector((state) => state.auth);

  useEffect(() => {
    const confettiTime = localStorage.getItem('confetti');
    if (!isEmpty(form) && confettiTime && window.confetti) {
      localStorage.removeItem('confetti');
      setTimeout(() => {
        window.confetti({
          particleCount: 100,
          spread: 90,
          origin: { x: 0.5, y: 0.5 },
          zIndex: 1001,
        });
      }, 0);
    }
    setWidgetMessage(
      localStorage.getItem('wf-msg') ?? t('share.welcomeMessageInput', 'Message on WhatsApp'),
    );
  }, [form, t]);

  const ShareToWhatsApp = (key) => (
    <Tooltip placement="top" title={t('common.shareWhatsapp', 'Share on WhatsApp')}>
      <a
        className="whatsapp-share"
        href={generateShareContent(key)}
        target="_blank"
        rel="noreferrer"
      >
        <WhatsAppOutlined />
      </a>
    </Tooltip>
  );

  const generateShareContent = (key) => {
    const text = t(`share.whatsapp.${key}`, { link: form.link });
    if (text) {
      return `https://api.whatsapp.com/send?phone=${form.whatsappNumber}&text=${encodeURI(text)}`;
    }
  };

  const menuContents = {
    link: (
      <div className="share-form-link">
        <div>
          <div>
            <Typography.Text className="sub-title">{t('share.social.linkTitle')}</Typography.Text>
            <Typography.Paragraph className="sub-desc">
              {t('share.social.linkDescription')}
            </Typography.Paragraph>
          </div>
          <div className="share-input">
            <div className="share-link-display">
              <Input
                disabled
                className="share-link"
                placeholder="link"
                value={`${getFormLink(me?.customDomain, form.link, form.customDomainLink)}`}
              />

              <CopyToClipboard
                key="1"
                text={`${getFormLink(me?.customDomain, form.link, form.customDomainLink)}`}
                onCopy={() => {
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 2000);
                }}
              >
                <Button type="primary">
                  {copied ? t('common.copied', 'Copied') : t('common.copy', 'Copy')}
                </Button>
              </CopyToClipboard>
            </div>
          </div>
        </div>
        <div>
          <Divider />
          <div className="share-social-section">
            <div>
              <Typography.Text className="sub-title">
                {t('share.social.socialTitle')}
              </Typography.Text>
              <Typography.Paragraph className="sub-desc">
                {t('share.social.socialDescription')}
              </Typography.Paragraph>
            </div>
            <div className="share-socials">
              <a
                className="share-fb"
                target="_blank"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURI(
                  'https://whatsform.com/' + form.link,
                )}`}
                rel="noreferrer"
              >
                <FacebookFilled />
              </a>
              <a
                className="share-tw"
                target="_blank"
                rel="noreferrer"
                href={`https://twitter.com/intent/tweet?url=${encodeURI(
                  'https://whatsform.com/' + form.link,
                )}`}
              >
                <TwitterOutlined size="large" />
              </a>

              <a
                className="share-li"
                target="_blank"
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURI(
                  'https://whatsform.com/' + form.link,
                )}`}
                rel="noreferrer"
              >
                <LinkedinFilled />
              </a>
            </div>
          </div>
        </div>
      </div>
    ),
    whatsapp: (
      <>
        <div>
          <Text className="sub-title">{t('share.whatsapp.title1')}</Text>

          <div className="message-container">
            <Text
              className="message-bubble"
              copyable={{
                text: t(
                  'share.whatsapp.copyMessage1',
                  `Thank you for your message 🙏

Please fill 👉 whatsform.com/{{link}} to share your requirements. One of our team members will assist you shortly.`,
                  { link: form.link },
                ),
                tooltips: [t('common.copy', 'Copy'), t('common.copied', 'Copied')],
                icon: <CopyOutlined />,
              }}
            >
              <Trans i18nKey="share.whatsapp.message1">
                <p>Thank you for your message 🙏</p>
                <p>
                  Please fill 👉
                  <a
                    href={encodeURI('https://whatsform.com/' + form.link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    whatsform.com/{{ link: form.link }}
                  </a>
                  to share your requirements. One of our team members will assist you shortly.
                </p>
              </Trans>
              {ShareToWhatsApp('copyMessage1')}
            </Text>
            <Text
              className="message-bubble"
              copyable={{
                text: t(
                  'share.whatsapp.copyMessage2',
                  `Welcome to yourBusinessName.

Our team is currently assisting another customer. Meanwhile, *please fill 👉 whatsform.com/{{link}}* to share your details

Thank you`,
                  { link: form.link },
                ),
                tooltips: [t('common.copy', 'Copy'), t('common.copied', 'Copied')],
                icon: <CopyOutlined />,
              }}
            >
              <Trans i18nKey="share.whatsapp.message2">
                <p>Welcome to yourBusinessName.</p>
                <p>
                  Our team is currently assisting another customer. Meanwhile, *please fill 👉
                  <a
                    href={encodeURI('https://whatsform.com/' + form.link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    whatsform.com/{{ link: form.link }}
                  </a>
                  * to share your details.
                </p>
                <p>Thank you</p>
              </Trans>
              {ShareToWhatsApp('copyMessage2')}
            </Text>
            <Text
              className="message-bubble"
              copyable={{
                text: t(
                  'share.whatsapp.copyMessage3',
                  `Hey there 👋\n\nIf you can help us by filling this form 👉 whatsform.com/{{link}} to share your details, we can help you 2x faster. It will take a minute!\n\nOnce you fill the form, one of our staff will assist you.`,
                  { link: form.link },
                ),
                tooltips: [t('common.copy', 'Copy'), t('common.copied', 'Copied')],
                icon: <CopyOutlined />,
              }}
            >
              <Trans i18nKey="share.whatsapp.message3">
                <p>Hey there 👋</p>
                <p>
                  If you can help us by filling this form 👉{' '}
                  <a
                    href={encodeURI('https://whatsform.com/' + form.link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    whatsform.com/{{ link: form.link }}
                  </a>{' '}
                  to share your details, we can help you 2x faster. It will take a minute!
                </p>
                <p>Once you fill the form, one of our staff will assist you.</p>
              </Trans>
              {ShareToWhatsApp('copyMessage3')}
            </Text>
            {/*    <Text
              className="message-bubble"
              copyable={{
                text: t('share.whatsapp.copyMessage5', { link: form.link }),
                tooltips: [t('common.copy', 'Copy'), t('common.copied', 'Copied')],
              }}
            >
              <Trans i18nKey="share.whatsapp.message6">
                <p>We acknowledge your message.</p>
                <p>
                  Before we reply, please consider filling 👉{' '}
                  <a
                    href={encodeURI('https://whatsform.com/' + form.link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    whatsform.com/{{ link: form.link }}
                  </a>
                  . This will help us, help you faster ⚡️
                </p>
                <p>Thank you for your co-operation.</p>
              </Trans>
              {ShareToWhatsApp('copyMessage5')}
            </Text> */}
          </div>
        </div>
        <div>
          <Divider />
          <Text className="sub-title">{t('share.whatsapp.title2')}</Text>

          <div className="message-container">
            <Text
              className="message-bubble"
              copyable={{
                text: t(
                  'share.whatsapp.copyMessage4',
                  `Thanks for contacting yourBusinessName!\n\nWe’d love to help, but we’re *currently closed* 🕧. We’ll come back to you the next working day ⏰. In the meantime, please fill 👉 whatsform.com/{{link}} to share your requirements.\n\nCheers,\nTeam yourBusinessName`,
                  { link: form.link },
                ),
                tooltips: [t('common.copy', 'Copy'), t('common.copied', 'Copied')],
                icon: <CopyOutlined />,
              }}
            >
              <Trans i18nKey="share.whatsapp.message4">
                <p>Thanks for contacting yourBusinessName!</p>
                <p>
                  We’d love to help, but we’re *currently closed* 🕧. We’ll come back to you the
                  next working day ⏰.
                </p>
                <p>
                  In the meantime, please fill 👉
                  <a
                    href={encodeURI('https://whatsform.com/' + form.link)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    whatsform.com/{{ link: form.link }}
                  </a>{' '}
                  to share your requirements
                </p>
                <p style={{ marginBottom: 0 }}>Cheers,</p>
                <p>Team yourBusinessName</p>
              </Trans>
              {ShareToWhatsApp('copyMessage4')}
            </Text>
          </div>
        </div>
      </>
    ),
    website: (
      <div className={'share-form-website'}>
        <div>
          <div>
            <Typography.Text className="sub-title">{t('share.website.embedTitle')}</Typography.Text>
            <Typography.Paragraph className="sub-desc">
              {t('share.website.embedDescription')}
            </Typography.Paragraph>
          </div>
          <div className="code-block-section">
            <pre className={'language-html'}>
              <code className={'language-html'}>
                {`<iframe src="https://whatsform.com/${form.link}"  width="100%" height="600" frameBorder="0"></iframe>`}
              </code>
            </pre>
            <CopyToClipboardButton
              text={`<iframe src="https://whatsform.com/${form.link}"  width="100%" height="600" frameBorder="0"></iframe>`}
            />
          </div>
        </div>
        <div>
          <Divider />
          <div>
            <Typography.Text className="sub-title">
              {t('share.website.widgetTitle')}
            </Typography.Text>
            <Typography.Paragraph className="sub-desc">
              {t('share.website.widgetDescription')}
            </Typography.Paragraph>
          </div>

          <div className="welcome-message">
            <label>{t('share.website.welcomeMessageLabel')}</label>
            <Input
              className="message-input"
              placeholder="Message on WhatsApp"
              value={widgetMessage}
              onChange={(e) => setWidgetMessage(e.target.value)}
              onBlur={(e) => localStorage.setItem('wf-msg', e.target.value)}
            />
            <Tooltip
              placement="right"
              title={t('share.website.welcomeMessageTooltip')}
              className="message-tooltip"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div className="code-block-section">
            {' '}
            <pre className={'language-html'}>
              <code className={'language-html'}>
                {`<script async src="https://whatsform.com/launcher.js" id="wf-widget" data-id="${form.link}" data-message="${widgetMessage}" ></script>`}
              </code>
            </pre>
            <CopyToClipboardButton
              text={`<script async src="https://whatsform.com/launcher.js" id="wf-widget" data-id="${form.link}" data-message="${widgetMessage}" ></script>`}
            />
          </div>
        </div>
      </div>
    ),
    gtm: (
      <div className={'share-form-website'}>
        <div>
          <div>
            <Typography.Text className="sub-title">
              {t('share.website.widgetTitle')}
            </Typography.Text>
            <Typography.Paragraph className="sub-desc">
              {t('share.gtm.widgetDescription')}
            </Typography.Paragraph>
          </div>

          <div className="welcome-message">
            <label>{t('share.website.welcomeMessageLabel')}</label>
            <Input
              className="message-input"
              placeholder="Message on WhatsApp"
              value={widgetMessage}
              onChange={(e) => setWidgetMessage(e.target.value)}
              onBlur={(e) => localStorage.setItem('wf-msg', e.target.value)}
            />
            <Tooltip
              placement="right"
              title={t('share.website.welcomeMessageTooltip')}
              className="message-tooltip"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div className="code-block-section">
            {' '}
            <pre className={'language-html'}>
              <code className={'language-html'}>
                {`<script>window.whatsformId = '${form.link}';${
                  widgetMessage && `window.whatsformMessage = '${widgetMessage}';`
                }</script><script async src="https://whatsform.com/launcher.js"></script>`}
              </code>
            </pre>
            <CopyToClipboardButton
              text={`<script>window.whatsformId = '${form.link}';${
                widgetMessage && `window.whatsformMessage = '${widgetMessage}';`
              }</script><script async src="https://whatsform.com/launcher.js"></script>`}
            />
          </div>
        </div>
      </div>
    ),
    wordpress: (
      <div className={'share-form-wordpress'}>
        <div>
          <div>
            <Typography.Text className="sub-title">
              {t('share.wordpress.install-form-to-chat-word')}
            </Typography.Text>
            <Typography.Paragraph className={'sub-desc'}>
              {t('share.wordpress.go-to-the-plugin-director')}
              {'  '}
              <a
                className="ant-btn ant-btn-primary"
                href="https://wordpress.org/plugins/form-to-chat/"
                target="_blank"
                rel="noreferrer"
              >
                {t('share.wordpress.wordpress-plugin-page')}
              </a>
            </Typography.Paragraph>
          </div>
        </div>
        <div>
          <Divider />
          <div>
            <Typography.Text className="sub-title">
              {t('share.wordpress.embed-in-a-page-or-post')}
            </Typography.Text>
            <Typography.Paragraph className="sub-desc">
              {t('share.wordpress.embedDesc')}
            </Typography.Paragraph>
          </div>
          <div className="code-block-section">
            <pre className={'language-html'}>
              <code className={'language-html'}>{`https://whatsform.com/${form.link}`}</code>
            </pre>
            <CopyToClipboardButton text={`https://whatsform.com/${form.link}`} />
          </div>
        </div>

        <div>
          <Divider />
          <div>
            <Typography.Text className="sub-title">
              {t('share.wordpress.add-as-widget')}
            </Typography.Text>
            <Typography.Paragraph className="sub-desc">
              {t('share.wordpress.copy-the-below-code-snipp')}
            </Typography.Paragraph>
          </div>
          <div className="welcome-message">
            <label>{t('share.website.welcomeMessageLabel')}</label>
            <Input
              className="message-input"
              placeholder="Message on WhatsApp"
              value={widgetMessage}
              onChange={(e) => setWidgetMessage(e.target.value)}
              onBlur={(e) => localStorage.setItem('wf-msg', e.target.value)}
            />
            <Tooltip
              placement="right"
              title={t('share.website.welcomeMessageTooltip')}
              className="message-tooltip"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
          <div className="code-block-section">
            <pre className={'language-html'}>
              <code className={'language-html'}>
                {`<script async src="https://whatsform.com/launcher.js" id="wf-widget" data-id="${form.link}" data-message="${widgetMessage}" ></script>`}
              </code>
            </pre>
            <CopyToClipboardButton
              text={`<script async src="https://whatsform.com/launcher.js" id="wf-widget" data-id="${form.link}" data-message="${widgetMessage}" ></script>`}
            />
          </div>
        </div>
      </div>
    ),
    qr: (
      <>
        <div className="qr-code-content">
          <Typography.Text className="sub-title">QR Code</Typography.Text>
          <Typography.Paragraph className="sub-desc">
            {t('share.qrcode.description')}
          </Typography.Paragraph>
          <div className="print-div">
            <img
              id="section-to-print"
              className="share-qr-image"
              src={`https://app.whatsform.com/api/forms/${form.link}/qr`}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                {t('common.print', 'Print')}
              </Button>
              <a target="_blank" href={`/api/forms/${form.link}/qr`} download rel="noreferrer">
                <Button icon={<CloudDownloadOutlined />} type="primary">
                  {t('common.download', 'Download')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </>
    ),
  };

  return (
    <section className="share-form-page-section">
      <ShareFormMenu selectedScreen={selectedScreen} setSelectedScreen={setSelectedScreen} />

      <div className="share-form-page-content">
        <div>{menuContents[selectedScreen]}</div>
      </div>
    </section>
  );
};

const CopyToClipboardButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      className="copy-button-code-block"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
      onClick={() => {
        const copied = copy(text);
        if (copied) {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        }
      }}
    ></Button>
  );
};

export default ShareForm;
